import { useCallback, useEffect, useState } from 'react';
import { History, RefreshCw, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import StatusPill from '../components/StatusPill';
import EmptyState from '../components/EmptyState';
import { secondaryButtonClass } from '../components/FilterBar';
import { formatDateTime } from '../lib/format';
import { fetchBackupStatus, type BackupComponentStatus, type BackupStatusResponse } from '../lib/api';

function BackupCard({ label, data }: { label: string; data: BackupComponentStatus }) {
  return (
    <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-zt-primary" size={18} />
          <h3 className="font-semibold capitalize text-white">{label} backups</h3>
        </div>
        <StatusPill
          status={data.reachable ? 'healthy' : 'error'}
          label={data.reachable ? 'Reachable' : 'Unreachable'}
        />
      </div>

      <dl className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-zt-muted">Last success</dt>
          <dd className="flex items-center gap-1.5 text-slate-200">
            {data.last_success ? (
              <>
                <CheckCircle2 size={14} className="text-zt-primary" />
                {formatDateTime(data.last_success)}
              </>
            ) : (
              <>
                <XCircle size={14} className="text-zt-muted" />
                No record
              </>
            )}
          </dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-zt-muted">Timer state</dt>
          <dd className="text-slate-200">{data.timer?.active_state ?? 'unknown'}</dd>
        </div>
        {data.last_error && (
          <div>
            <dt className="mb-1 text-zt-muted">Last error</dt>
            <dd className="rounded-lg bg-zt-danger/10 px-3 py-2 font-mono text-xs text-zt-danger">
              {data.last_error}
            </dd>
          </div>
        )}
        {data.log_tail?.length > 0 && (
          <div>
            <dt className="mb-1 text-zt-muted">Recent log</dt>
            <dd>
              <pre className="max-h-40 overflow-auto rounded-lg bg-zt-bg p-3 font-mono text-xs text-zt-muted">
                {data.log_tail.join('\n')}
              </pre>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function Backups() {
  const [data, setData] = useState<BackupStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchBackupStatus();
    if (result.ok && result.data) setData(result.data);
    else setData(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const bothHealthy = data ? data.general.reachable && data.legal.reachable : false;

  return (
    <PageShell
      title="Backups"
      subtitle="Restic backup health for general and legal (compliance) data sets."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Overall health"
          value={loading ? '…' : bothHealthy ? 'Healthy' : 'Needs attention'}
          tone={bothHealthy ? 'success' : 'danger'}
          icon={<History size={18} />}
        />
        <StatCard
          label="General reachable"
          value={loading ? '…' : data?.general.reachable ? 'Yes' : 'No'}
          tone={data?.general.reachable ? 'success' : 'danger'}
        />
        <StatCard
          label="Legal reachable"
          value={loading ? '…' : data?.legal.reachable ? 'Yes' : 'No'}
          tone={data?.legal.reachable ? 'success' : 'danger'}
        />
        <StatCard
          label="Checked"
          value={loading ? '…' : data ? formatDateTime(data.timestamp) : '—'}
          tone="neutral"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-zt-muted">Backup targets and timer status</div>
        <button type="button" onClick={load} className={secondaryButtonClass}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-xl border border-zt-border bg-zt-surface" />
          ))}
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <BackupCard label="general" data={data.general} />
          <BackupCard label="legal" data={data.legal} />
        </div>
      ) : (
        <EmptyState
          icon={<History size={32} />}
          title="Backup status unavailable"
          message="Could not read backup timers. This endpoint reads systemd timers on the host."
        />
      )}
    </PageShell>
  );
}

export default Backups;
